import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, X, Plus, RotateCcw, AlertCircle, Undo2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

/**
 * P12b — per-company sub-niche editor for one macro-category.
 *
 * The backend (P12a) owns everything that matters:
 *   - the country -> language mapping. We send a COUNTRY and read `lang` back.
 *     There is deliberately NO country->language map in the frontend.
 *   - all term validation. We send the terms and display the message the API
 *     returns; no rule is reimplemented here.
 *
 * This component edits the DIFF (`added` / `removed`), never the list itself, and
 * never sends `base` or `effective` back. The effective list shown while editing is
 * derived locally for DISPLAY only — the authoritative one always comes from the API.
 */
export const SubnicheEditorDialog = ({ open, onOpenChange, macro, country, onSaved }) => {
  const { token, isAdmin } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // 'ok' | 'no-presets' | 'unexpected'
  const [status, setStatus] = useState('ok');
  const [lang, setLang] = useState(null);

  // Server truth for this macro.
  const [base, setBase] = useState([]);
  const [serverAdded, setServerAdded] = useState([]);
  const [serverRemoved, setServerRemoved] = useState([]);

  // Local working copy of the diff.
  const [added, setAdded] = useState([]);
  const [removed, setRemoved] = useState([]);

  const [newTerm, setNewTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);

  const macroKey = macro?.key;

  const load = useCallback(async () => {
    if (!token || !country || !macroKey) return;
    setLoading(true);
    setLoadError(null);
    setSaveError(null);
    setConfirmingReset(false);
    try {
      // Fetched without macro_key so that "this language has no presets at all"
      // arrives as an empty `macros` array rather than a 404 we would have to
      // recognise by parsing its message.
      const res = await axios.get(`${API_URL}/subniche-overrides`, {
        params: { country },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLang(res.data.lang);

      const macros = res.data.macros || [];
      if (macros.length === 0) {
        setStatus('no-presets');
        return;
      }

      const view = macros.find(m => m.macro_key === macroKey);
      if (!view || !Array.isArray(view.effective) || view.effective.length === 0) {
        // A macro that is returned must always carry a non-empty effective list.
        setStatus('unexpected');
        return;
      }

      setStatus('ok');
      setBase(view.base || []);
      setServerAdded(view.added || []);
      setServerRemoved(view.removed || []);
      setAdded(view.added || []);
      setRemoved(view.removed || []);
    } catch (error) {
      setLoadError(
        error.response?.data?.detail || 'Could not load the sub-niches for this category.'
      );
    } finally {
      setLoading(false);
    }
  }, [token, country, macroKey]);

  useEffect(() => {
    if (open) {
      load();
    } else {
      setNewTerm('');
      setSaveError(null);
      setConfirmingReset(false);
    }
  }, [open, load]);

  // --- Diff transitions (UI state only — no normalisation, no validation) ---

  const removeBaseTerm = (term) => setRemoved([...removed, term]);
  const removeCustomTerm = (term) => setAdded(added.filter(t => t !== term));
  const restoreBaseTerm = (term) => {
    setRemoved(removed.filter(t => t !== term));
    // A term can sit in both lists if it was removed and then re-added by hand.
    setAdded(added.filter(t => t !== term));
  };
  const addTerm = () => {
    if (!newTerm.trim()) return;
    setAdded([...added, newTerm]);
    setNewTerm('');
    setSaveError(null);
  };

  // Display-only projection of what a search would use right now.
  const effective = [...base.filter(t => !removed.includes(t)), ...added];
  const removedBaseTerms = base.filter(t => removed.includes(t));

  const dirty =
    JSON.stringify(added) !== JSON.stringify(serverAdded) ||
    JSON.stringify(removed) !== JSON.stringify(serverRemoved);
  const customised = serverAdded.length > 0 || serverRemoved.length > 0;

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await axios.put(
        `${API_URL}/subniche-overrides`,
        { country, macro_key: macroKey, added, removed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBase(res.data.base || []);
      setServerAdded(res.data.added || []);
      setServerRemoved(res.data.removed || []);
      setAdded(res.data.added || []);
      setRemoved(res.data.removed || []);
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (error) {
      // Every rule lives in the backend — show its own words.
      setSaveError(error.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await axios.delete(`${API_URL}/subniche-overrides`, {
        params: { country, macro_key: macroKey },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (onSaved) onSaved();
      await load();
    } catch (error) {
      setSaveError(error.response?.data?.detail || 'Could not reset. Please try again.');
    } finally {
      setSaving(false);
      setConfirmingReset(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="subniche-editor">
        <DialogHeader>
          <DialogTitle className="text-white">
            Sub-niches — {macro?.label}
          </DialogTitle>
          <DialogDescription>
            {country}
            {lang ? ` · market language: ${lang}` : ''}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center gap-2 text-slate-400" data-testid="subniche-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading sub-niches...
          </div>
        )}

        {!loading && loadError && (
          <div className="py-6 flex items-start gap-2 text-sm text-red-300" data-testid="subniche-load-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{loadError}</span>
          </div>
        )}

        {!loading && !loadError && status === 'no-presets' && (
          <div className="py-6 text-sm text-slate-300" data-testid="subniche-no-presets">
            <p className="font-medium text-white mb-1">No sub-niche presets for this market yet.</p>
            <p className="text-slate-400">
              CiaoLead only has curated sub-niche lists for some markets so far
              {lang ? ` (this one resolves to “${lang}”)` : ''}. Searches here use the
              category term directly, and there is nothing to customise.
            </p>
          </div>
        )}

        {!loading && !loadError && status === 'unexpected' && (
          <div className="py-6 flex items-start gap-2 text-sm text-red-300" data-testid="subniche-unexpected">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              This category came back without a usable sub-niche list. Nothing has been
              changed — please try again, and report it if it persists.
            </span>
          </div>
        )}

        {!loading && !loadError && status === 'ok' && (
          <div className="space-y-5">
            {/* The budget is fixed. Without saying so, a shorter list reads as "fewer searches". */}
            <p className="text-xs text-slate-400 bg-white/5 border border-white/10 rounded-md p-3">
              Every category always runs <strong className="text-slate-200">5 searches</strong>,
              whatever the length of this list. With fewer than 5 sub-niches CiaoLead searches
              them more deeply; with more than 5 it rotates through a different 5 each time.
              Editing this list changes <em>what</em> is searched, never how much.
            </p>

            {!isAdmin && (
              <p className="text-xs text-amber-300" data-testid="subniche-readonly-note">
                You can view this list, but only a company admin can change it.
              </p>
            )}

            <div>
              <p className="text-sm font-medium text-slate-200 mb-2">
                Searched for this category ({effective.length})
              </p>
              <div className="flex flex-wrap gap-2" data-testid="subniche-effective">
                {effective.map((term, i) => {
                  const isCustom = added.includes(term);
                  return (
                    <Badge
                      key={`${term}-${i}`}
                      variant="secondary"
                      className={`flex items-center gap-1 ${isCustom ? 'border-blue-500/40 text-blue-200' : ''}`}
                    >
                      {term}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => (isCustom ? removeCustomTerm(term) : removeBaseTerm(term))}
                          className="ml-1 hover:text-red-400"
                          aria-label={`Remove ${term}`}
                          data-testid={`subniche-remove-${term}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {removedBaseTerms.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-200 mb-2">
                  Removed from the default list ({removedBaseTerms.length})
                </p>
                <div className="flex flex-wrap gap-2" data-testid="subniche-removed">
                  {removedBaseTerms.map(term => (
                    <Badge key={term} variant="outline" className="flex items-center gap-1 text-slate-500 line-through">
                      {term}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => restoreBaseTerm(term)}
                          className="ml-1 no-underline hover:text-blue-300"
                          aria-label={`Restore ${term}`}
                          data-testid={`subniche-restore-${term}`}
                        >
                          <Undo2 className="h-3 w-3" />
                        </button>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {isAdmin && (
              <div>
                <p className="text-sm font-medium text-slate-200 mb-2">Add a sub-niche</p>
                <div className="flex gap-2">
                  <Input
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTerm();
                      }
                    }}
                    placeholder="e.g. robot aspirapolvere"
                    data-testid="subniche-new-term"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addTerm}
                    disabled={!newTerm.trim()}
                    data-testid="subniche-add"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Use the words a shopper would search in this market. Terms are checked when
                  you save.
                </p>
              </div>
            )}

            {saveError && (
              <div className="flex items-start gap-2 text-sm text-red-300" data-testid="subniche-save-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{saveError}</span>
              </div>
            )}

            {confirmingReset && (
              <div className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-md p-3 space-y-2" data-testid="subniche-reset-confirm">
                <p>
                  This discards your company’s customisation of <strong>{macro?.label}</strong> for
                  this market and restores CiaoLead’s default list of {base.length} sub-niches.
                  Other categories and other markets are not affected.
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="destructive" size="sm" onClick={handleReset}
                          disabled={saving} data-testid="subniche-reset-confirm-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                    Yes, reset to default
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                          onClick={() => setConfirmingReset(false)} disabled={saving}>
                    Keep my changes
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {isAdmin && status === 'ok' && !loading && !loadError && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmingReset(true)}
              disabled={saving || confirmingReset || !customised}
              className="sm:mr-auto"
              data-testid="subniche-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset to default
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {isAdmin && status === 'ok' && !loading && !loadError && (
            <Button
              type="button"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="world-btn-primary"
              data-testid="subniche-save"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Save
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubnicheEditorDialog;
